import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api";
import type { MeResponse, Org } from "../types";
import { clearClientState, onRevoked } from "./clientState";

interface SessionCtx {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<MeResponse | null>;
  setOrg: (org: Org | null) => void;
  /** Pilot access was removed (403 {code: "revoked"}): the app shows the "access removed" screen. */
  revoked: boolean;
  clearRevoked: () => void;
  /** Forgets the signed-in user and all client state at once (after deleting company data). */
  signOutLocal: () => void;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [revoked, setRevoked] = useState(false);
  const handling = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      return m;
    } catch {
      const empty = { user: null, org: null, is_admin: false };
      setMe(empty);
      return empty;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOutLocal = useCallback(() => {
    clearClientState();
    setMe((m) => ({ user: null, org: null, is_admin: false, home: null, pilot: m?.pilot }));
    setLoading(false);
  }, []);

  // pilot access removed: forget everything in the browser and end the session cookie too
  useEffect(
    () =>
      onRevoked(() => {
        signOutLocal();
        setRevoked(true);
        if (handling.current) return; // several calls can fail at once; log out only once
        handling.current = true;
        void api
          .logout()
          .catch(() => undefined)
          .finally(() => {
            handling.current = false;
          });
      }),
    [signOutLocal],
  );

  const setOrg = useCallback((org: Org | null) => {
    setMe((m) => (m ? { ...m, org } : m));
  }, []);

  const clearRevoked = useCallback(() => setRevoked(false), []);

  return (
    <Ctx.Provider value={{ me, loading, refresh, setOrg, revoked, clearRevoked, signOutLocal }}>{children}</Ctx.Provider>
  );
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession outside SessionProvider");
  return c;
}
